/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Operations › Support, rebuilt on the kit (overhaul plan step 3f): the same calls and the same state as before, with
 * the five work areas that were one scroll now four views in the page header (Devices, Requests, Sessions, OTA
 * releases, each its own `?view=`), the new-session form and a session's detail in a side drawer, and the request a
 * desktop notification deep-linked to (`/support?request=<id>`) still highlighted and scrolled into view under Requests.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BellRing, CheckCircle2, CircleStop, Inbox, Info, KeyRound, Monitor, Package, RefreshCw, Send, ShieldCheck, Stethoscope, Terminal, Trash2, TriangleAlert, Wifi, WifiOff, X,
} from 'lucide-react';
import {
  api,
  ApiError,
  type DeviceEnrollmentAuthorization,
  type DeviceKeyMigration,
  type OtaProposal,
  type SupportCommand,
  type SupportDelivery,
  type SupportDevice,
  type SupportRequest,
  type SupportSessionDetail,
  type SupportSessionMode,
  type SupportSessionState,
  type SupportSessionSummary,
} from '../lib/api';
import {
  activeOtaDeviceTargets,
  buildOtaRelease,
  type OtaChannel,
  type OtaReleaseType,
} from '../lib/ota-release';
import { describeDeviceHealthNote } from '../lib/deviceHealth';
import { supportRequestFromSearch } from '../lib/deepLink';
import { QUARANTINE_RESTORE_STEPS, readmitQuarantineNotice, revokeAllConfirmText } from '../lib/deviceQuarantine';
import { navigate, usePlace } from '../shell/router';
import {
  Button, Callout, Checkbox, Drawer, EmptyState, Field, Grid, Icon, Input, NumberInput, PageHeader, Panel, Select, Skeleton, Stack, StatusChip, Table, Tag, Textarea,
  type Column, type Tone,
} from '../ui';
import { count, dateTime } from '../ui/format';

const fmtTime = (ms?: number) => (ms ? dateTime(ms) : '—');
const fmtAge = (ms?: number) => {
  if (!ms) return '—';
  const s = Math.max(0, Date.now() - ms) / 1000;
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};
const fmtUntil = (ms: number) => {
  const minutes = Math.max(0, Math.ceil((ms - Date.now()) / 60_000));
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.ceil(minutes / 60)}h`;
  return `${Math.ceil(minutes / 1440)}d`;
};
const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'request failed');

/** Session state → chip tone (a distinct tone per state). */
const STATE_TONE: Record<SupportSessionState, Tone> = {
  requested: 'info',
  'awaiting-consent': 'warn',
  active: 'ok',
  suspended: 'accent',
  ended: 'neutral',
  denied: 'bad',
};
const StateBadge = ({ state }: { state: SupportSessionState }) => <StatusChip tone={STATE_TONE[state]}>{state}</StatusChip>;

/** The agent's diagnostic line (today: machine folder trust) under the health chip, in plain words; the line as sent is the tooltip. */
const HealthNote = ({ lastError }: { lastError?: string }) => {
  const note = describeDeviceHealthNote(lastError);
  if (!note) return null;
  const label = note.tone === 'bad' ? 'refused' : note.tone === 'warn' ? 'folder check' : 'note';
  return (
    <span className="ui-cell-sub ui-health-note" title={note.raw}>
      <StatusChip tone={note.tone === 'muted' ? 'neutral' : note.tone}>{label}</StatusChip> <span>{note.text}</span>
    </span>
  );
};

const CMD_TONE: Record<string, Tone> = { pending: 'neutral', acked: 'info', completed: 'ok', failed: 'bad', timedout: 'warn' };
const REQ_TONE: Record<SupportRequest['status'], Tone> = { open: 'warn', claimed: 'info', resolved: 'neutral' };
const OTA_TONE: Record<OtaProposal['status'], Tone> = { published: 'ok', approved: 'info', publishing: 'warn', pending: 'neutral' };

const MODES: Array<{ id: SupportSessionMode; label: string }> = [
  { id: 'attended-consent', label: 'Attended consent — user must explicitly accept' },
  { id: 'notice-then-proceed', label: 'Notice, then proceed — user is notified, session starts' },
  { id: 'auto-accept-silent', label: 'Auto-accept (silent) — no user interaction' },
];

/** The read-only collector allow-list (mirror of the service's SUPPORT_COLLECTORS). */
const COLLECTORS = [
  { tool: 'get_system_info', label: 'System info' },
  { tool: 'get_network_state', label: 'Network state' },
  { tool: 'get_event_log', label: 'Event log' },
  { tool: 'get_service_status', label: 'Service status' },
  { tool: 'get_process_list', label: 'Process list' },
  { tool: 'get_disk_health', label: 'Disk health' },
];

/** The page's views, each its own `?view=` so it can be linked to; the deep link `?request=` opens Requests. */
type View = 'devices' | 'requests' | 'sessions' | 'ota';
const VIEWS: readonly View[] = ['devices', 'requests', 'sessions', 'ota'];
const isView = (value: string | null): value is View => (VIEWS as readonly string[]).includes(value ?? '');

interface FormState {
  requestId: string;
  deviceId: string;
  targetUserEmail: string;
  reason: string;
  requestedMode: SupportSessionMode;
  country: string;
  region: string;
}
interface FormRefusal {
  message: string;
  requiredMode?: string;
  legalBasis?: string;
}

interface OtaFormState {
  version: string;
  packageUrl: string;
  sha256: string;
  sizeBytes: string;
  type: OtaReleaseType;
  channel: OtaChannel;
  targetMode: 'all' | 'selected';
  selectedDeviceIds: string[];
}

/** A device with its id and name, as the tables show one. */
const DeviceCell = ({ name, id }: { name?: string; id: string }) => (
  <span className="ui-cell-stack">
    <span>{name ?? '—'}</span>
    <Tag title={id}>{id}</Tag>
  </span>
);

/** A key fingerprint, shortened, with the whole as the tooltip. */
const Fingerprint = ({ sha, length = 16 }: { sha: string; length?: number }) => <Tag title={sha}>{sha.slice(0, length)}…</Tag>;

export function Support() {
  const place = usePlace();
  const [devices, setDevices] = useState<SupportDevice[]>([]);
  const [authorizations, setAuthorizations] = useState<DeviceEnrollmentAuthorization[]>([]);
  const [migrations, setMigrations] = useState<DeviceKeyMigration[]>([]);
  const [showAuthorize, setShowAuthorize] = useState(false);
  const [authorizationBusy, setAuthorizationBusy] = useState(false);
  const [authorizationForm, setAuthorizationForm] = useState<{
    deviceId: string;
    machineName: string;
    publicKeyPem: string;
    purpose: 'user' | 'broker-audit';
    replacesPublicKeySha256: string;
    expiresHours: string;
  }>({ deviceId: '', machineName: '', publicKeyPem: '', purpose: 'user', replacesPublicKeySha256: '', expiresHours: '24' });
  // OTA publish (its own view)
  const [otaBusy, setOtaBusy] = useState(false);
  const [otaResult, setOtaResult] = useState('');
  const [otaProposals, setOtaProposals] = useState<OtaProposal[]>([]);
  const [otaForm, setOtaForm] = useState<OtaFormState>({
    version: '',
    packageUrl: '',
    sha256: '',
    sizeBytes: '',
    type: 'update-available',
    channel: 'canary',
    targetMode: 'selected',
    selectedDeviceIds: [],
  });
  const otaTargets = useMemo(() => activeOtaDeviceTargets(devices), [devices]);
  const otaTargetSelectionValid = useMemo(() => {
    if (otaTargets.length === 0 || otaTargets.length > 500) return false;
    if (otaForm.targetMode === 'all') return true;
    const active = new Set(otaTargets.map((target) => target.deviceId));
    return otaForm.selectedDeviceIds.length > 0
      && otaForm.selectedDeviceIds.length <= 500
      && otaForm.selectedDeviceIds.every((deviceId) => active.has(deviceId));
  }, [otaForm.selectedDeviceIds, otaForm.targetMode, otaTargets]);
  const proposeOta = async () => {
    setOtaBusy(true);
    setOtaResult('');
    try {
      const release = buildOtaRelease({
        type: otaForm.type,
        version: otaForm.version,
        packageUrl: otaForm.packageUrl,
        sha256: otaForm.sha256,
        sizeBytes: Number(otaForm.sizeBytes),
        channel: otaForm.channel,
        targetMode: otaForm.targetMode,
        selectedDeviceIds: otaForm.selectedDeviceIds,
        activeDeviceIds: otaTargets.map((target) => target.deviceId),
      });
      const r = await api.otaPropose({ release });
      setOtaProposals((current) => [r.proposal, ...current.filter((item) => item.proposalId !== r.proposal.proposalId)]);
      setOtaResult('Proposal recorded. A different administrator must inspect and approve it before publish.');
    } catch (e) {
      setOtaResult(`proposal failed: ${errMsg(e)}`);
    } finally {
      setOtaBusy(false);
    }
  };
  const toggleOtaTarget = (deviceId: string) => {
    setOtaForm((current) => ({
      ...current,
      selectedDeviceIds: current.selectedDeviceIds.includes(deviceId)
        ? current.selectedDeviceIds.filter((candidate) => candidate !== deviceId)
        : [...current.selectedDeviceIds, deviceId],
    }));
  };
  const approveOta = async (proposalId: string) => {
    setOtaBusy(true);
    setOtaResult('');
    try {
      const r = await api.otaApprove(proposalId);
      setOtaProposals((current) => current.map((item) => item.proposalId === proposalId ? r.proposal : item));
      setOtaResult('Proposal approved. It is ready for health-gated publish.');
    } catch (e) {
      setOtaResult(`approval failed: ${errMsg(e)}`);
    } finally {
      setOtaBusy(false);
    }
  };
  const publishApprovedOta = async (proposalId: string) => {
    setOtaBusy(true);
    setOtaResult('');
    try {
      const r = await api.otaPublish(proposalId);
      setOtaResult(`${r.queued} durably pending; ${r.alreadyAccepted} already accepted from durable custody; ${r.accepted}/${r.targets} accepted total. ${r.attempted} attempted this publish, ${r.delivered} reached a live channel, and ${r.applied} are acknowledged applied.`);
      const refreshed = await api.otaProposals();
      setOtaProposals(refreshed.proposals);
    } catch (e) {
      setOtaResult(`publish failed: ${errMsg(e)}`);
    } finally {
      setOtaBusy(false);
    }
  };
  const [sessions, setSessions] = useState<SupportSessionSummary[]>([]);
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [reqBusy, setReqBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The request a desktop IT-support card linked to (`?request=<id>`): its row is highlighted and scrolled into
  // view once the queue has loaded. Read once; a later refresh keeps the highlight, a dismiss clears it.
  const [focusRequestId, setFocusRequestId] = useState<string | null>(() => supportRequestFromSearch(location.search));
  const focusScrolled = useRef(false);
  const focusRequest = focusRequestId ? requests.find((r) => r.requestId === focusRequestId) ?? null : null;

  // Which view shows: `?view=`, else Requests when a request was deep-linked, else Devices.
  const params = new URLSearchParams(place.search);
  const asked = params.get('view');
  const view: View = isView(asked) ? asked : focusRequestId ? 'requests' : 'devices';
  const hrefFor = (id: View) => {
    const next = new URLSearchParams(place.search);
    next.set('view', id);
    return `/support?${next.toString()}`;
  };

  useEffect(() => {
    if (!focusRequestId || focusScrolled.current || view !== 'requests') return;
    const row = document.getElementById(`support-request-${focusRequestId}`);
    if (!row) return;
    focusScrolled.current = true;
    row.scrollIntoView({ block: 'center', inline: 'nearest' });
  }, [focusRequestId, requests, view]);

  // Last known push-delivery result per session (from create / notify — the list endpoint doesn't carry it).
  const [deliveries, setDeliveries] = useState<Record<string, SupportDelivery>>({});

  // New-session form
  const [form, setForm] = useState<FormState | null>(null);
  const [formRefusal, setFormRefusal] = useState<FormRefusal | null>(null);
  const [creating, setCreating] = useState(false);

  // Detail
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SupportSessionDetail | null>(null);
  const detailRef = useRef<SupportSessionDetail | null>(null);
  useEffect(() => {
    detailRef.current = detail;
  }, [detail]);

  // Command form
  const [cmdTool, setCmdTool] = useState('get_system_info');
  const [evtLog, setEvtLog] = useState<'System' | 'Application'>('System');
  const [evtMax, setEvtMax] = useState('50');
  const [svcName, setSvcName] = useState('');
  const [procTop, setProcTop] = useState('15');
  const [issuing, setIssuing] = useState(false);
  const [cmdError, setCmdError] = useState<string | null>(null);
  const [cmdDelivery, setCmdDelivery] = useState<SupportDelivery | null>(null);

  // Server-side diagnosis gate. Raw telemetry never enters browser state.
  const [gating, setGating] = useState(false);
  const [gateError, setGateError] = useState<string | null>(null);
  const [gateResult, setGateResult] = useState<Awaited<ReturnType<typeof api.gateSupportDiagnosis>> | null>(null);

  const loadLists = useCallback(async () => {
    setError(null);
    // Each list loads independently. Remote support (sessions/requests) and OTA are separately feature-gated on
    // the service and answer 404 when off; that must not blank the device-custody panel, which is the one IT
    // needs to authorize an enrollment. A 404 is "feature off", anything else is a real error.
    const [d, s, r, o] = await Promise.allSettled([api.supportDevices(), api.supportSessions(), api.supportRequests(), api.otaProposals()]);
    if (d.status === 'fulfilled') {
      setDevices(d.value.devices);
      setAuthorizations(d.value.authorizations);
      setMigrations(d.value.migrations);
    }
    if (s.status === 'fulfilled') setSessions(s.value.sessions);
    if (r.status === 'fulfilled') setRequests(r.value.requests);
    if (o.status === 'fulfilled') setOtaProposals(o.value.proposals);
    const failures = [
      ['device custody', d],
      ['support sessions', s],
      ['support requests', r],
      ['OTA proposals', o],
    ] as const;
    const real = failures.filter(([, res]) => res.status === 'rejected' && !(res.reason instanceof ApiError && res.reason.status === 404));
    if (real.length) setError(real.map(([label, res]) => `${label}: ${res.status === 'rejected' ? errMsg(res.reason) : ''}`).join(' · '));
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadLists();
  }, [loadLists]);

  const loadDetail = useCallback(async (id: string) => {
    try {
      const d = await api.supportSession(id);
      setDetail(d);
      // Keep the list row in sync (state / telemetryCount / pendingCommands move as we poll).
      setSessions((prev) => prev.map((s) => (s.sessionId === id ? d.session : s)));
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setSelectedId(null);
      else setError(errMsg(e));
    }
  }, []);

  // Select → load detail + poll every 3 s while the session can still move (awaiting consent, pending
  // commands, or suspended waiting on a result). Terminal or idle-active sessions don't poll.
  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setDetail(null);
    setCmdError(null);
    setCmdDelivery(null);
    setGateError(null);
    setGateResult(null);
    void loadDetail(selectedId);
    const t = setInterval(() => {
      const s = detailRef.current?.session;
      if (!s) return;
      const live = s.state === 'requested' || s.state === 'awaiting-consent' || s.state === 'suspended' || (s.state === 'active' && s.pendingCommands > 0);
      if (live) void loadDetail(selectedId);
    }, 3000);
    return () => clearInterval(t);
  }, [selectedId, loadDetail]);

  // ---- Actions ----

  const revokeDevice = async (d: SupportDevice) => {
    const name = d.machineName ? `${d.machineName} (${d.deviceId})` : d.deviceId;
    // Re-authorizing alone does not restore the PC: the agent quarantines itself (lib/deviceQuarantine.ts).
    if (!window.confirm(revokeAllConfirmText(name))) return;
    try {
      await api.revokeSupportDevice(d.deviceId, 'revoked from Support console');
      await loadLists();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const revokeDeviceKey = async (d: SupportDevice) => {
    const shortKey = d.publicKeySha256.slice(0, 16);
    if (!window.confirm(`Revoke only key ${shortKey}… on ${d.machineName ?? d.deviceId}? Other enrolled user keys stay active.`)) return;
    try {
      await api.revokeSupportDeviceKey(d.deviceId, d.publicKeySha256, 'exact key revoked from Support console');
      await loadLists();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const authorizeDevice = async () => {
    const expiresHours = Number(authorizationForm.expiresHours);
    if (!authorizationForm.deviceId.trim() || !authorizationForm.publicKeyPem.trim() || !Number.isFinite(expiresHours)) return;
    setAuthorizationBusy(true);
    setError(null);
    try {
      await api.authorizeSupportDevice({
        deviceId: authorizationForm.deviceId.trim(),
        machineName: authorizationForm.machineName.trim() || undefined,
        publicKeyPem: authorizationForm.publicKeyPem.trim(),
        purpose: authorizationForm.purpose,
        replacesPublicKeySha256: authorizationForm.replacesPublicKeySha256 || undefined,
        expiresInMs: Math.round(expiresHours * 60 * 60_000),
      });
      setAuthorizationForm({ deviceId: '', machineName: '', publicKeyPem: '', purpose: 'user', replacesPublicKeySha256: '', expiresHours: '24' });
      setShowAuthorize(false);
      await loadLists();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setAuthorizationBusy(false);
    }
  };

  const revokeAuthorization = async (authorization: DeviceEnrollmentAuthorization) => {
    if (!window.confirm(`Revoke the pending enrollment authorization for ${authorization.deviceId}?`)) return;
    try {
      await api.revokeSupportDeviceAuthorization(authorization.authorizationId);
      await loadLists();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const startForm = (requestId: string, deviceId: string, prefill?: { targetUserEmail?: string; reason?: string }) => {
    setForm({ requestId, deviceId, targetUserEmail: prefill?.targetUserEmail ?? '', reason: prefill?.reason ?? '', requestedMode: 'attended-consent', country: 'US', region: 'AZ' });
    setFormRefusal(null);
  };

  // Claim a user-raised request → the new-session form pre-fills with its device + summary-as-reason.
  const claimRequest = async (r: SupportRequest) => {
    setReqBusy(r.requestId);
    try {
      const res = await api.claimSupportRequest(r.requestId);
      setRequests((prev) => prev.map((x) => (x.requestId === r.requestId ? res.request : x)));
      startForm(r.requestId, res.session.targetDeviceId, { targetUserEmail: res.session.targetUserEmail, reason: res.session.reason });
    } catch (e) {
      setError(errMsg(e));
      await loadLists(); // e.g. someone else claimed it — resync
    } finally {
      setReqBusy(null);
    }
  };

  const resolveRequest = async (r: SupportRequest) => {
    setReqBusy(r.requestId);
    try {
      const res = await api.resolveSupportRequest(r.requestId);
      setRequests((prev) => prev.map((x) => (x.requestId === r.requestId ? res.request : x)));
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setReqBusy(null);
    }
  };

  const createSession = async () => {
    if (!form) return;
    setFormRefusal(null);
    setCreating(true);
    try {
      const r = await api.createSupportSession({
        requestId: form.requestId,
        reason: form.reason.trim(),
        requestedMode: form.requestedMode,
        jurisdiction: { country: form.country.trim().toUpperCase(), region: form.region.trim() ? form.region.trim().toUpperCase() : undefined },
      });
      setDeliveries((p) => ({ ...p, [r.session.sessionId]: r.delivery }));
      setSessions((p) => [r.session, ...p.filter((s) => s.sessionId !== r.session.sessionId)]);
      setForm(null);
      setSelectedId(r.session.sessionId);
      navigate(hrefFor('sessions'));
    } catch (e) {
      if (e instanceof ApiError && e.body?.error === 'mode_not_permitted') {
        setFormRefusal({
          message: typeof e.body.message === 'string' ? e.body.message : 'The requested mode is not permitted in this jurisdiction.',
          requiredMode: typeof e.body.requiredMode === 'string' ? e.body.requiredMode : undefined,
          legalBasis: typeof e.body.legalBasis === 'string' ? e.body.legalBasis : undefined,
        });
      } else if (e instanceof ApiError && e.body?.error === 'device_not_enrolled') {
        setFormRefusal({ message: 'That device is no longer enrolled — refresh the device list.' });
      } else {
        setFormRefusal({ message: errMsg(e) });
      }
    } finally {
      setCreating(false);
    }
  };

  const renotify = async () => {
    if (!selectedId) return;
    try {
      const r = await api.notifySupportSession(selectedId);
      setDeliveries((p) => ({ ...p, [selectedId]: r.delivery }));
      setDetail((d) => (d ? { ...d, session: r.session } : d));
      setSessions((prev) => prev.map((s) => (s.sessionId === selectedId ? r.session : s)));
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const buildArgs = (): Record<string, unknown> | undefined => {
    if (cmdTool === 'get_event_log') {
      const a: Record<string, unknown> = { log: evtLog };
      if (evtMax.trim() && Number.isFinite(Number(evtMax))) a.max = Number(evtMax);
      return a;
    }
    if (cmdTool === 'get_service_status') return svcName.trim() ? { name: svcName.trim() } : undefined;
    if (cmdTool === 'get_process_list') return procTop.trim() && Number.isFinite(Number(procTop)) ? { top: Number(procTop) } : undefined;
    return undefined;
  };

  const issueCommand = async () => {
    if (!selectedId) return;
    setCmdError(null);
    setCmdDelivery(null);
    setIssuing(true);
    try {
      const r = await api.issueSupportCommand(selectedId, { tool: cmdTool, args: buildArgs() });
      setCmdDelivery(r.delivery);
      await loadDetail(selectedId);
    } catch (e) {
      setCmdError(errMsg(e));
    } finally {
      setIssuing(false);
    }
  };

  const runGate = async () => {
    if (!selectedId) return;
    setGateError(null);
    setGateResult(null);
    setGating(true);
    try {
      setGateResult(await api.gateSupportDiagnosis(selectedId));
      await loadDetail(selectedId);
    } catch (e) {
      setGateError(errMsg(e));
    } finally {
      setGating(false);
    }
  };

  const endSession = async () => {
    if (!selectedId) return;
    if (!window.confirm('End this support session? This is terminal — a new session (and fresh consent) is needed to continue.')) return;
    try {
      const r = await api.endSupportSession(selectedId);
      setDetail((d) => (d ? { ...d, session: r.session } : d));
      setSessions((prev) => prev.map((s) => (s.sessionId === selectedId ? r.session : s)));
    } catch (e) {
      setError(errMsg(e));
    }
  };

  // ---- Render ----

  const s = detail?.session;
  const delivery = s ? deliveries[s.sessionId] : undefined;
  const awaitingConsent = s && (s.state === 'requested' || s.state === 'awaiting-consent');
  const openRequests = requests.filter((r) => r.status === 'open').length;
  const activeDevices = devices.filter((d) => d.status === 'active').length;
  // Re-admitting a PC whose keys were revoked with "Revoke all" does not lift its quarantine: say so where IT re-admits.
  const readmitNotice = readmitQuarantineNotice(devices, authorizationForm.deviceId);

  const authorizationColumns: Array<Column<DeviceEnrollmentAuthorization>> = [
    { key: 'device', header: 'Device', cell: (a) => <DeviceCell name={a.machineName} id={a.deviceId} /> },
    { key: 'purpose', header: 'Purpose', cell: (a) => <StatusChip tone="neutral" plain>{a.purpose}</StatusChip> },
    { key: 'fingerprint', header: 'Fingerprint', cell: (a) => <Fingerprint sha={a.publicKeySha256} /> },
    { key: 'intent', header: 'Custody intent', muted: true, cell: (a) => (a.replacesPublicKeySha256 ? <>replaces <Fingerprint sha={a.replacesPublicKeySha256} length={12} /></> : 'independent key') },
    { key: 'by', header: 'Authorized by', muted: true, hideOnPhone: true, cell: (a) => a.authorizedByEmail },
    { key: 'expires', header: 'Expires', muted: true, cell: (a) => `in ${fmtUntil(a.expiresAtMs)}` },
    {
      key: 'actions', header: <span className="ui-sr">Actions</span>, align: 'end', width: '56px',
      cell: (a) => <Button variant="ghost" size="sm" icon={Trash2} title="Revoke pending authorization" aria-label={`Revoke the pending authorization for ${a.deviceId}`} onClick={() => void revokeAuthorization(a)} />,
    },
  ];

  const migrationColumns: Array<Column<DeviceKeyMigration>> = [
    { key: 'device', header: 'Device', cell: (m) => <DeviceCell name={m.machineName} id={m.deviceId} /> },
    { key: 'user', header: 'User', muted: true, cell: (m) => m.subjectEmail },
    { key: 'purpose', header: 'Purpose', cell: (m) => <StatusChip tone="neutral" plain>{m.purpose}</StatusChip> },
    { key: 'keys', header: 'Key transition', cell: (m) => <><Fingerprint sha={m.oldPublicKeySha256} length={12} /> → <Fingerprint sha={m.newPublicKeySha256} length={12} /></> },
    { key: 'phase', header: 'Phase', cell: (m) => <StatusChip tone={m.status === 'retired' ? 'ok' : 'info'}>{m.status}</StatusChip> },
    { key: 'updated', header: 'Updated', muted: true, cell: (m) => fmtAge(m.retiredAtMs ?? m.verifiedAtMs ?? m.acceptedAtMs) },
  ];

  const deviceColumns: Array<Column<SupportDevice>> = [
    {
      key: 'device', header: 'Device',
      cell: (d) => (
        <span className="ui-cell-stack">
          <span className="ui-nowrap">{d.machineName ?? '—'}</span>
          <Tag title={d.deviceId}>{d.deviceId}</Tag>
          <span className="ui-cell-sub">enrolled {fmtAge(d.enrolledAtMs)}</span>
        </span>
      ),
    },
    {
      key: 'key', header: 'Key',
      cell: (d) => (
        <span className="ui-cell-stack">
          <Fingerprint sha={d.publicKeySha256} />
          <span><StatusChip tone="neutral" plain>{d.purpose}</StatusChip></span>
        </span>
      ),
    },
    {
      key: 'owner', header: 'Owner', muted: true, hideOnPhone: true,
      cell: (d) => (
        <span className="ui-cell-stack">
          <span title={d.ownerEmail ? 'Last signed-in user to enroll this key' : 'No signed-in user has enrolled this key yet'}>{d.ownerEmail ?? '—'}</span>
          <span className="ui-cell-sub">enrolled by {d.enrolledBy ?? '—'}</span>
        </span>
      ),
    },
    {
      key: 'health', header: 'Agent and health',
      cell: (d) => (d.health ? (
        <span className="ui-cell-stack">
          <span className="ui-actions"><span className="ui-nowrap">v{d.health.version}</span><StatusChip tone={d.health.healthy ? 'ok' : 'bad'}>{d.health.healthy ? 'healthy' : 'unhealthy'} · {fmtAge(d.health.at)}</StatusChip></span>
          <HealthNote lastError={d.health.lastError} />
        </span>
      ) : <StatusChip tone="neutral">no report</StatusChip>),
    },
    { key: 'status', header: 'Status', cell: (d) => <StatusChip tone={d.status === 'active' ? 'ok' : 'bad'}>{d.status}</StatusChip> },
    {
      key: 'actions', header: <span className="ui-sr">Actions</span>, align: 'end',
      cell: (d) => (
        <span className="ui-row-actions">
          {d.status === 'active' && (
            <Button variant="ghost" size="sm" icon={CircleStop} title="Revoke only this exact enrolled key" onClick={() => void revokeDeviceKey(d)}>Revoke key</Button>
          )}
          {d.status === 'active' && devices.filter((candidate) => candidate.deviceId === d.deviceId && candidate.status === 'active').length > 1 && (
            <Button variant="danger" size="sm" icon={Trash2} title="Revoke every key for this physical device; Gyro on the PC quarantines itself until IT lifts it" onClick={() => void revokeDevice(d)}>Revoke all</Button>
          )}
        </span>
      ),
    },
  ];

  const requestColumns: Array<Column<SupportRequest>> = [
    { key: 'status', header: 'Status', cell: (r) => <StatusChip tone={REQ_TONE[r.status]}>{r.status}</StatusChip> },
    // User-typed content: rendered strictly as text.
    { key: 'summary', header: 'Summary', width: '30%', cell: (r) => <span className="ui-clip" title={r.summary}>{r.summary}</span> },
    { key: 'device', header: 'Device', cell: (r) => <DeviceCell name={r.machineName} id={r.deviceId} /> },
    {
      key: 'people', header: 'User / operator', muted: true,
      cell: (r) => <span className="ui-cell-stack"><span>{r.userEmail ?? '—'}</span><span className="ui-cell-sub">{r.claimedBy ? `operator ${r.claimedBy}` : 'no operator yet'}</span></span>,
    },
    { key: 'age', header: 'Age', numeric: true, muted: true, cell: (r) => fmtAge(r.createdAtMs) },
    {
      key: 'actions', header: <span className="ui-sr">Actions</span>, align: 'end',
      cell: (r) => (
        <span className="ui-row-actions">
          {r.status === 'open' && (
            <Button variant="primary" size="sm" icon={Send} busy={reqBusy === r.requestId} title="Claim this request — pre-fills a new session for its device" onClick={() => void claimRequest(r)}>Claim</Button>
          )}
          {r.status !== 'resolved' && (
            <Button variant="ghost" size="sm" icon={CheckCircle2} disabled={reqBusy === r.requestId} title="Mark this request resolved" onClick={() => void resolveRequest(r)}>Resolve</Button>
          )}
        </span>
      ),
    },
  ];

  const sessionColumns: Array<Column<SupportSessionSummary>> = [
    { key: 'state', header: 'State', cell: (row) => <StateBadge state={row.state} /> },
    { key: 'reason', header: 'Reason', width: '28%', cell: (row) => <span className="ui-clip" title={row.reason}>{row.reason}</span> },
    { key: 'device', header: 'Device', cell: (row) => <Tag title={row.targetDeviceId}>{row.targetDeviceId}</Tag> },
    {
      key: 'people', header: 'Target user / operator', muted: true,
      cell: (row) => <span className="ui-cell-stack"><span>{row.targetUserEmail ?? '—'}</span><span className="ui-cell-sub">operator {row.operatorEmail}</span></span>,
    },
    { key: 'telemetry', header: 'Telemetry', numeric: true, cell: (row) => count(row.telemetryCount) },
    { key: 'pending', header: 'Pending', numeric: true, cell: (row) => (row.pendingCommands > 0 ? <StatusChip tone="warn">{row.pendingCommands}</StatusChip> : '0') },
    { key: 'age', header: 'Age', numeric: true, muted: true, cell: (row) => fmtAge(row.createdAtMs) },
  ];

  const commandColumns: Array<Column<SupportCommand>> = [
    { key: 'seq', header: 'Seq', numeric: true, muted: true, width: '56px', cell: (c) => c.seq },
    { key: 'tool', header: 'Tool', cell: (c) => <Tag>{c.tool}</Tag> },
    { key: 'args', header: 'Args', muted: true, mono: true, cell: (c) => (c.args !== undefined && c.args !== null ? JSON.stringify(c.args) : '—') },
    { key: 'status', header: 'Status', cell: (c) => <StatusChip tone={CMD_TONE[c.status] ?? 'neutral'}>{c.status}</StatusChip> },
    { key: 'issued', header: 'Issued', muted: true, cell: (c) => fmtAge(c.issuedAtMs) },
    { key: 'error', header: 'Error', cell: (c) => (c.error ? <span className="ui-text-bad">{c.error}</span> : '—') },
  ];

  const otaColumns: Array<Column<OtaProposal>> = [
    { key: 'status', header: 'Status', cell: (p) => <StatusChip tone={OTA_TONE[p.status]}>{p.status}</StatusChip> },
    { key: 'release', header: 'Release', cell: (p) => <span className="ui-cell-stack"><span>v{p.review.manifest.version}</span><span className="ui-cell-sub">{p.review.type} · {p.review.manifest.kind}</span></span> },
    { key: 'package', header: 'Package', width: '24%', cell: (p) => <span className="ui-cell-stack"><span className="ui-break is-mono">{p.review.manifest.packageLocation}</span>{p.review.manifest.releaseNotes && <span className="ui-cell-sub">{p.review.manifest.releaseNotes}</span>}</span> },
    {
      key: 'hash', header: 'Hash / size', hideOnPhone: true, width: '22%',
      cell: (p) => (
        <span className="ui-cell-stack">
          <span className="ui-break is-mono">{p.review.manifest.sha256}</span>
          <span className="ui-cell-sub">{p.review.manifest.sizeBytes.toLocaleString()} bytes</span>
          <span className="ui-cell-sub">envelope <span className="ui-break is-mono">{p.releaseSha256}</span></span>
        </span>
      ),
    },
    {
      key: 'scope', header: 'Scope / policy', hideOnPhone: true,
      cell: (p) => (
        <span className="ui-cell-stack">
          <span>{p.review.manifest.channel} · mandatory {p.review.manifest.mandatory ? 'yes' : 'no'} · rollback {p.review.manifest.rollback ? 'yes' : 'no'}</span>
          <span className="ui-cell-sub">from {p.review.manifest.minFromVersion ?? 'any version'}</span>
          <span className="ui-cell-sub ui-break is-mono" title={p.review.deviceIds === 'all' ? 'all active user devices' : p.review.deviceIds.join(', ')}>
            {p.review.deviceIds === 'all' ? 'all active user devices' : p.review.deviceIds.join(', ')}
          </span>
        </span>
      ),
    },
    { key: 'who', header: 'Maker / checker', cell: (p) => <span className="ui-cell-stack"><span className="ui-break">{p.createdByEmail}</span><span className="ui-cell-sub">{p.approvedByEmail ?? 'awaiting independent approval'}</span></span> },
    {
      key: 'action', header: <span className="ui-sr">Action</span>, align: 'end',
      cell: (p) => (
        <>
          {p.status === 'pending' && <Button variant="primary" size="sm" icon={ShieldCheck} disabled={otaBusy} onClick={() => void approveOta(p.proposalId)}>Approve</Button>}
          {p.status === 'approved' && <Button variant="primary" size="sm" icon={Send} disabled={otaBusy} onClick={() => void publishApprovedOta(p.proposalId)}>Publish</Button>}
        </>
      ),
    },
  ];

  const tabs = [
    { id: 'devices', label: 'Devices', href: hrefFor('devices'), count: activeDevices },
    { id: 'requests', label: 'Requests', href: hrefFor('requests'), count: openRequests },
    { id: 'sessions', label: 'Sessions', href: hrefFor('sessions'), count: sessions.length },
    { id: 'ota', label: 'OTA releases', href: hrefFor('ota'), count: otaProposals.filter((p) => p.status !== 'published').length },
  ];

  return (
    <>
      <PageHeader
        meta={<span>Remote diagnostic sessions against enrolled desktop devices: consent-gated per jurisdiction, read-only collectors only</span>}
        actions={<Button icon={RefreshCw} onClick={() => void loadLists()} aria-label="Refresh" className="is-collapse-phone">Refresh</Button>}
        tabs={tabs}
        activeTab={view}
      />
      <div className="ui-page">
        <Stack>
          {error && (
            <Callout tone="bad" role="alert" title="The service refused" action={<Button variant="ghost" size="sm" icon={X} aria-label="Dismiss" onClick={() => setError(null)} />}>
              {error}
            </Callout>
          )}

          {view === 'devices' && (
            <>
              <Panel
                flush
                title={<span className="ui-title-icon"><Icon icon={Monitor} size="md" />Device identities</span>}
                description={loading ? 'Loading…' : `${count(activeDevices)} active of ${count(devices.length)} keys`}
                actions={(
                  <Button variant="primary" size="sm" icon={KeyRound} aria-expanded={showAuthorize} onClick={() => setShowAuthorize((value) => !value)}>Authorize enrollment</Button>
                )}
                footer={(
                  <>
                    <Icon icon={Info} size="sm" />
                    <span>
                      Support sessions begin from a user-raised request. Revocation retains custody history; new or replacement keys require an exact admin authorization.
                      {' '}Revoke all also quarantines Gyro on the PC (agents newer than 0.4.1.0), and re-authorizing it does not lift that: {QUARANTINE_RESTORE_STEPS}
                    </span>
                  </>
                )}
              >
                {showAuthorize && (
                  <div className="ui-panel-pad ui-panel-pad--divided">
                    <div className="ui-form">
                      <Grid cols={4}>
                        <Field label="Canonical device ID">
                          <Input value={authorizationForm.deviceId} onChange={(e) => setAuthorizationForm({ ...authorizationForm, deviceId: e.target.value })} placeholder="Entra device ID" />
                        </Field>
                        <Field label="Machine name">
                          <Input value={authorizationForm.machineName} onChange={(e) => setAuthorizationForm({ ...authorizationForm, machineName: e.target.value })} placeholder="Optional" />
                        </Field>
                        <Field label="Key purpose">
                          <Select value={authorizationForm.purpose} onChange={(e) => setAuthorizationForm({ ...authorizationForm, purpose: e.target.value as 'user' | 'broker-audit', replacesPublicKeySha256: '' })}>
                            <option value="user">User identity</option>
                            <option value="broker-audit">Broker audit</option>
                          </Select>
                        </Field>
                        <Field label="Expires in hours">
                          <NumberInput type="number" min="1" max="168" value={authorizationForm.expiresHours} onChange={(e) => setAuthorizationForm({ ...authorizationForm, expiresHours: e.target.value })} />
                        </Field>
                      </Grid>
                      <Field label="Custody intent">
                        <Select value={authorizationForm.replacesPublicKeySha256} onChange={(e) => setAuthorizationForm({ ...authorizationForm, replacesPublicKeySha256: e.target.value })}>
                          <option value="">Add an independent key</option>
                          {devices
                            .filter((device) => device.deviceId === authorizationForm.deviceId.trim() && device.purpose === authorizationForm.purpose && device.status === 'active')
                            .map((device) => (
                              <option key={`${device.deviceId}:${device.publicKeySha256}`} value={device.publicKeySha256}>
                                Replace {device.publicKeySha256.slice(0, 16)}… ({device.ownerEmail ?? device.enrolledBy ?? 'unknown owner'})
                              </option>
                            ))}
                        </Select>
                      </Field>
                      <Field label="Device ES256 public key (PEM)">
                        <Textarea rows={5} className="is-mono" value={authorizationForm.publicKeyPem} onChange={(e) => setAuthorizationForm({ ...authorizationForm, publicKeyPem: e.target.value })} placeholder="-----BEGIN PUBLIC KEY-----" />
                      </Field>
                      {readmitNotice && (
                        <Callout tone="warn" role="alert" icon={TriangleAlert}>{readmitNotice}</Callout>
                      )}
                      <div className="ui-actions">
                        <Button variant="primary" icon={ShieldCheck} busy={authorizationBusy} disabled={!authorizationForm.deviceId.trim() || !authorizationForm.publicKeyPem.trim()} onClick={() => void authorizeDevice()}>Authorize exact key</Button>
                        <Button variant="ghost" disabled={authorizationBusy} onClick={() => setShowAuthorize(false)}>Cancel</Button>
                      </div>
                    </div>
                  </div>
                )}
                <Table
                  label="Device identities"
                  columns={deviceColumns}
                  rows={devices}
                  rowKey={(d) => `${d.deviceId}:${d.publicKeySha256}`}
                  loading={loading}
                  skeletonRows={4}
                  className="ui-table--tall"
                  empty={<EmptyState compact icon={Monitor} title="No device identities recorded">Authorize an exact device ID and public key before its first connection.</EmptyState>}
                  phone={{
                    title: (d) => `${d.machineName ?? d.deviceId} · ${d.purpose}`,
                    meta: (d) => `${d.ownerEmail ?? d.enrolledBy ?? '—'} · ${d.health ? `v${d.health.version}, ${d.health.healthy ? 'healthy' : 'unhealthy'}` : 'no report'}`,
                    value: (d) => <StatusChip tone={d.status === 'active' ? 'ok' : 'bad'}>{d.status}</StatusChip>,
                    sub: (d) => (d.status === 'active' ? <Button variant="ghost" size="sm" icon={CircleStop} onClick={() => void revokeDeviceKey(d)}>Revoke key</Button> : null),
                  }}
                />
              </Panel>

              {authorizations.length > 0 && (
                <Panel flush title="Pending authorizations" description="Keys IT has authorized that have not enrolled yet">
                  <Table stacked label="Pending authorizations" columns={authorizationColumns} rows={authorizations} rowKey={(a) => a.authorizationId} stickyHeader={false} className="ui-table--tall" />
                </Panel>
              )}

              {migrations.length > 0 && (
                <Panel flush title="TPM migrations" description="Keys moving from one TPM to another">
                  <Table stacked label="TPM migrations" columns={migrationColumns} rows={migrations} rowKey={(m) => m.migrationId} stickyHeader={false} className="ui-table--tall" />
                </Panel>
              )}
            </>
          )}

          {view === 'requests' && (
            <>
              {focusRequestId && !loading && (
                <Callout
                  tone="info"
                  icon={BellRing}
                  role="status"
                  action={<Button variant="ghost" size="sm" icon={X} aria-label="Clear the highlight" title="Clear the highlight" onClick={() => setFocusRequestId(null)} />}
                >
                  {focusRequest ? (
                    <>
                      Opened from a notification — request <code>{focusRequestId.slice(0, 8)}</code> is highlighted below
                      {focusRequest.status !== 'open' && focusRequest.claimedBy ? ` (${focusRequest.status}, operator ${focusRequest.claimedBy})` : ''}.
                    </>
                  ) : (
                    <>
                      Request <code>{focusRequestId.slice(0, 8)}</code> from the notification is no longer in the queue (resolved or expired).
                    </>
                  )}
                </Callout>
              )}
              <Panel
                flush
                title={<span className="ui-title-icon"><Icon icon={Inbox} size="md" />Requests</span>}
                description="Raised by people from their desktop agent; an operator claims one, then starts a session"
                actions={openRequests > 0 ? <StatusChip tone="warn">{openRequests} open</StatusChip> : <StatusChip tone="neutral">0 open</StatusChip>}
                footer={<><Icon icon={Info} size="sm" /><span><strong>Claim</strong> takes ownership and pre-fills the new-session form with the request&apos;s device and summary; <strong>Resolve</strong> closes it.</span></>}
              >
                <Table
                  label="Support requests"
                  columns={requestColumns}
                  rows={requests}
                  rowKey={(r) => r.requestId}
                  rowId={(r) => `support-request-${r.requestId}`}
                  selectedKey={focusRequestId}
                  loading={loading}
                  skeletonRows={3}
                  empty={<EmptyState compact icon={Inbox} title="No support requests">Users can raise a hand from their desktop agent; requests queue here for an operator to claim.</EmptyState>}
                  phone={{
                    title: (r) => r.summary,
                    meta: (r) => `${r.machineName ?? r.deviceId} · ${r.userEmail ?? '—'} · ${fmtAge(r.createdAtMs)}`,
                    value: (r) => <StatusChip tone={REQ_TONE[r.status]}>{r.status}</StatusChip>,
                    sub: (r) => (
                      <span className="ui-row-actions">
                        {r.status === 'open' && <Button variant="primary" size="sm" busy={reqBusy === r.requestId} onClick={() => void claimRequest(r)}>Claim</Button>}
                        {r.status !== 'resolved' && <Button variant="ghost" size="sm" disabled={reqBusy === r.requestId} onClick={() => void resolveRequest(r)}>Resolve</Button>}
                      </span>
                    ),
                  }}
                />
              </Panel>
            </>
          )}

          {view === 'sessions' && (
            <Panel
              flush
              title="Sessions"
              description="Select a session to see its consent, commands, telemetry and diagnosis"
              actions={<StatusChip tone="neutral" plain>{count(sessions.length)}</StatusChip>}
            >
              <Table
                label="Support sessions"
                columns={sessionColumns}
                rows={sessions}
                rowKey={(row) => row.sessionId}
                selectedKey={selectedId}
                onRowClick={(row) => setSelectedId(row.sessionId === selectedId ? null : row.sessionId)}
                loading={loading}
                skeletonRows={3}
                empty={<EmptyState compact icon={Terminal} title="No support sessions yet">Claim a request under Requests to start one.</EmptyState>}
                phone={{
                  title: (row) => row.reason,
                  meta: (row) => `${row.targetDeviceId} · ${row.targetUserEmail ?? '—'} · ${fmtAge(row.createdAtMs)}`,
                  value: (row) => <StateBadge state={row.state} />,
                  sub: (row) => <Button variant="ghost" size="sm" onClick={() => setSelectedId(row.sessionId)}>Open</Button>,
                }}
              />
            </Panel>
          )}

          {view === 'ota' && (
            <>
              {/* OTA publish — sign a release manifest and push it to enrolled devices. The device-side
                  broker independently re-verifies the ES256 signature + SHA-256 pin before installing. */}
              <Panel
                title={<span className="ui-title-icon"><Icon icon={Package} size="md" />Publish an OTA package</span>}
                description="A release manifest, signed by the service and pushed to enrolled devices; a different administrator approves it"
              >
                <div className="ui-form">
                  <Grid cols={4}>
                    <Field label="Version">
                      <Input value={otaForm.version} onChange={(e) => setOtaForm({ ...otaForm, version: e.target.value })} placeholder="0.3.1.0" />
                    </Field>
                    <Field label="Mode">
                      <Select value={otaForm.type} onChange={(e) => setOtaForm({ ...otaForm, type: e.target.value as OtaReleaseType })}>
                        <option value="update-available">Stage only</option>
                        <option value="update-now">Install now</option>
                        <option value="avatar-pack">Install avatar pack</option>
                      </Select>
                    </Field>
                    <Field label="Channel">
                      <Select value={otaForm.channel} onChange={(e) => setOtaForm({ ...otaForm, channel: e.target.value as OtaChannel })}>
                        <option value="canary">Canary</option>
                        <option value="early">Early</option>
                        <option value="broad">Broad</option>
                      </Select>
                    </Field>
                    <Field label="Targets">
                      <Select value={otaForm.targetMode} onChange={(e) => setOtaForm({ ...otaForm, targetMode: e.target.value as 'all' | 'selected' })}>
                        <option value="selected">Selected devices</option>
                        <option value="all">All active user devices</option>
                      </Select>
                    </Field>
                  </Grid>
                  <Field label="Package URL">
                    <Input
                      className="is-mono"
                      value={otaForm.packageUrl}
                      onChange={(e) => setOtaForm({ ...otaForm, packageUrl: e.target.value })}
                      placeholder={otaForm.type === 'avatar-pack' ? 'https://…/avatar-pack.zip?…' : 'https://…/Gyro.Agent_0.3.1.0_x64.msix?…'}
                    />
                  </Field>
                  <Grid cols={2}>
                    <Field label="SHA-256">
                      <Input className="is-mono" value={otaForm.sha256} onChange={(e) => setOtaForm({ ...otaForm, sha256: e.target.value })} placeholder="64 hexadecimal characters" />
                    </Field>
                    <Field label="Bytes">
                      <NumberInput type="number" min="1" step="1" value={otaForm.sizeBytes} onChange={(e) => setOtaForm({ ...otaForm, sizeBytes: e.target.value })} />
                    </Field>
                  </Grid>
                  {otaForm.targetMode === 'selected' ? (
                    <Field label="Devices" help={otaTargets.length === 0 ? 'No active user devices' : `${count(otaForm.selectedDeviceIds.length)} of ${count(otaTargets.length)} selected`}>
                      <div className="ui-check-grid">
                        {otaTargets.map((target) => (
                          <Checkbox key={target.deviceId} label={target.label} checked={otaForm.selectedDeviceIds.includes(target.deviceId)} onChange={() => toggleOtaTarget(target.deviceId)} />
                        ))}
                      </div>
                    </Field>
                  ) : (
                    <p className="ui-cell-sub">{count(otaTargets.length)} active user devices</p>
                  )}
                  <div className="ui-actions">
                    <Button
                      variant="primary"
                      icon={ShieldCheck}
                      busy={otaBusy}
                      disabled={!otaTargetSelectionValid || !otaForm.version || !otaForm.packageUrl || !/^[0-9a-fA-F]{64}$/.test(otaForm.sha256) || !Number.isSafeInteger(Number(otaForm.sizeBytes)) || Number(otaForm.sizeBytes) < 1}
                      onClick={() => void proposeOta()}
                    >
                      Create proposal
                    </Button>
                  </div>
                  {otaResult && <Callout tone={/failed:/.test(otaResult) ? 'bad' : 'ok'} role="status">{otaResult}</Callout>}
                </div>
              </Panel>
              <Panel flush title="Proposals" description="Maker and checker: the proposer may not approve their own">
                <Table stacked
                  label="OTA proposals"
                  columns={otaColumns}
                  rows={otaProposals.slice(0, 20)}
                  rowKey={(p) => p.proposalId}
                  loading={loading}
                  skeletonRows={2}
                  className="ui-table--tall"
                  empty={<EmptyState compact icon={Package} title="No proposals yet">Create one above; it is listed here for a second administrator to approve.</EmptyState>}
                />
              </Panel>
            </>
          )}
        </Stack>
      </div>

      {/* New-session form: opens when a request is claimed. */}
      <Drawer
        open={form !== null}
        onClose={() => setForm(null)}
        title="New support session"
        description={form ? `Device ${form.deviceId}` : undefined}
        footer={form ? (
          <>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
            <Button variant="primary" icon={Send} busy={creating} disabled={!form.reason.trim()} onClick={() => void createSession()}>Request session</Button>
          </>
        ) : undefined}
      >
        {form && (
          <div className="ui-form">
            {formRefusal && (
              <Callout tone="warn" role="alert" title={formRefusal.requiredMode ? 'Consent gate refused this mode' : 'Session refused'}>
                <p className="ui-para">{formRefusal.message}</p>
                {formRefusal.requiredMode && (
                  <p className="ui-para">
                    This jurisdiction requires at least <strong><code>{formRefusal.requiredMode}</code></strong>
                    {formRefusal.legalBasis && <> (legal basis <code>{formRefusal.legalBasis}</code>)</>}
                    . Change the requested mode and try again.
                  </p>
                )}
              </Callout>
            )}
            <Field label="Target device">
              <Input value={form.deviceId} readOnly disabled />
            </Field>
            <Field label="Target user email (optional)">
              <Input placeholder="user@exante360.com" value={form.targetUserEmail} onChange={(e) => setForm({ ...form, targetUserEmail: e.target.value })} />
            </Field>
            <Field label="Reason (required)" help="Shown verbatim to the target user in the consent prompt, and audited.">
              <Input placeholder="e.g. Ticket #4821 — investigating repeated BSOD on wake" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </Field>
            <Field label="Requested mode" help="The target's jurisdiction may require a stricter mode — the gate fails closed before the device is touched.">
              <Select value={form.requestedMode} onChange={(e) => setForm({ ...form, requestedMode: e.target.value as SupportSessionMode })}>
                {MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </Select>
            </Field>
            <Grid cols={2}>
              <Field label="Country">
                <Input placeholder="US" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
              </Field>
              <Field label="Region / state">
                <Input placeholder="AZ" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
              </Field>
            </Grid>
          </div>
        )}
      </Drawer>

      {/* Session detail: the selected session, polled while it can still move. */}
      <Drawer
        open={selectedId !== null}
        onClose={() => setSelectedId(null)}
        title={s ? <span className="ui-title-icon">Session <code>{s.sessionId.slice(0, 8)}</code> <StateBadge state={s.state} /></span> : 'Session'}
        description={s ? `“${s.reason}”` : 'Loading the session…'}
        className="ui-drawer--wide"
        footer={s && s.state !== 'ended' && s.state !== 'denied' ? (
          <Button variant="danger" icon={CircleStop} onClick={() => void endSession()}>End session</Button>
        ) : undefined}
      >
        {!s || !detail ? (
          <div className="ui-form" aria-busy="true"><Skeleton size="lg" /><Skeleton size="lg" /><Skeleton size="xl" /></div>
        ) : (
          <div className="ui-form">
            {/* Facts */}
            <dl className="ui-facts">
              <dt>Device</dt><dd><Tag>{s.targetDeviceId}</Tag></dd>
              <dt>Target user</dt><dd>{s.targetUserEmail ?? '—'}</dd>
              <dt>Operator</dt><dd>{s.operatorEmail}</dd>
              <dt>Mode</dt><dd><code>{s.requestedMode}</code></dd>
              <dt>Legal basis</dt><dd><code>{s.legalBasis}</code></dd>
              <dt>Created</dt><dd>{fmtTime(s.createdAtMs)}</dd>
              {s.endReason && <><dt>End reason</dt><dd><code>{s.endReason}</code></dd></>}
            </dl>

            {/* Consent + delivery */}
            <div className="ui-actions">
              {s.consent ? (
                <StatusChip tone={s.consent.decision === 'granted' ? 'ok' : 'bad'} icon={ShieldCheck}>
                  consent {s.consent.decision}{s.consent.byEmail ? ` by ${s.consent.byEmail}` : ''} · {fmtTime(s.consent.atMs)}
                </StatusChip>
              ) : awaitingConsent ? (
                <StatusChip tone="warn">awaiting user consent</StatusChip>
              ) : (
                <StatusChip tone="neutral">no consent recorded</StatusChip>
              )}
              {delivery && (delivery.ok
                ? <StatusChip tone="ok" icon={Wifi}>delivered</StatusChip>
                : <StatusChip tone="warn" icon={WifiOff}>not delivered · {delivery.outcome}</StatusChip>)}
              {awaitingConsent && <Button size="sm" icon={BellRing} onClick={() => void renotify()}>Re-notify</Button>}
            </div>
            {delivery && !delivery.ok && (
              <Callout tone="warn" icon={TriangleAlert}>Device offline — frame not delivered ({delivery.outcome}). Re-notify once the device reconnects.</Callout>
            )}

            {/* Commands */}
            <section className="ui-section" aria-labelledby="support-collectors">
              <h3 id="support-collectors" className="ui-section-title"><Icon icon={Terminal} size="sm" /> Read-only collectors</h3>
              {s.state === 'active' ? (
                <div className="ui-actions">
                  <Select size="sm" aria-label="Collector" value={cmdTool} onChange={(e) => setCmdTool(e.target.value)} className="ui-w-md">
                    {COLLECTORS.map((c) => <option key={c.tool} value={c.tool}>{c.label} ({c.tool})</option>)}
                  </Select>
                  {cmdTool === 'get_event_log' && (
                    <>
                      <Select size="sm" aria-label="Event log" value={evtLog} onChange={(e) => setEvtLog(e.target.value as 'System' | 'Application')} className="ui-w-sm">
                        <option value="System">System</option>
                        <option value="Application">Application</option>
                      </Select>
                      <NumberInput size="sm" type="number" min={1} placeholder="max" title="max events" aria-label="Max events" value={evtMax} onChange={(e) => setEvtMax(e.target.value)} className="ui-w-xs" />
                    </>
                  )}
                  {cmdTool === 'get_service_status' && (
                    <Input size="sm" placeholder="service name (optional)" aria-label="Service name" value={svcName} onChange={(e) => setSvcName(e.target.value)} className="ui-w-md" />
                  )}
                  {cmdTool === 'get_process_list' && (
                    <NumberInput size="sm" type="number" min={1} placeholder="top" title="top N processes" aria-label="Top N processes" value={procTop} onChange={(e) => setProcTop(e.target.value)} className="ui-w-xs" />
                  )}
                  <Button variant="primary" size="sm" icon={Send} busy={issuing} onClick={() => void issueCommand()}>Issue</Button>
                </div>
              ) : (
                <p className="ui-cell-sub">Commands can only be issued while the session is <strong>active</strong> (consent granted).</p>
              )}
              {cmdError && <Callout tone="bad" role="alert">{cmdError}</Callout>}
              {cmdDelivery && !cmdDelivery.ok && (
                <Callout tone="warn" icon={TriangleAlert}>Command frame not delivered ({cmdDelivery.outcome}) — the device is offline; the command will time out unless it reconnects.</Callout>
              )}
              {detail.commands.length > 0 && (
                <div className="ui-table-box">
                  <Table stacked label="Commands" columns={commandColumns} rows={detail.commands} rowKey={(c) => c.commandId} stickyHeader={false} />
                </div>
              )}
            </section>

            {/* Telemetry */}
            <section className="ui-section" aria-labelledby="support-telemetry">
              <h3 id="support-telemetry" className="ui-section-title">Telemetry ({detail.telemetry.length})</h3>
              <p className="ui-cell-sub">Remote values remain server-side. This view exposes collector provenance and status only.</p>
              {detail.telemetry.length === 0 ? (
                <p className="ui-cell-sub">No telemetry yet — issue a collector command above.</p>
              ) : (
                <div className="ui-details-list">
                  {detail.telemetry.map((t, i) => (
                    <details key={`${t.id}-${i}`} className="ui-details">
                      <summary>
                        <StatusChip tone={t.status === 'ok' ? 'ok' : 'warn'}>{t.status}</StatusChip> <Tag>{t.collector}</Tag>{' '}
                        <span className="ui-cell-sub">
                          {t.id}
                          {typeof t.ageMs === 'number' ? ` · age ${Math.round(t.ageMs / 1000)}s` : ''}
                        </span>
                      </summary>
                      <div className="ui-details-body">Evidence value retained in the governed support service.</div>
                    </details>
                  ))}
                </div>
              )}
            </section>

            {/* Diagnosis gate */}
            <section className="ui-section" aria-labelledby="support-gate">
              <h3 id="support-gate" className="ui-section-title"><Icon icon={Stethoscope} size="sm" /> Diagnosis gate</h3>
              <p className="ui-cell-sub">Gyro analyzes fenced telemetry inside the service. Coverage comes from the durable command ledger; model output cannot lower policy thresholds.</p>
              <div className="ui-actions">
                <Button variant="primary" size="sm" icon={ShieldCheck} busy={gating} disabled={detail.telemetry.length === 0} onClick={() => void runGate()}>Generate diagnosis</Button>
                {gateError && <span className="ui-text-bad">{gateError}</span>}
              </div>
              {gateResult && (
                <div className="ui-form">
                  <div className="ui-actions">
                    <StatusChip tone={gateResult.gate.action === 'apply-fix' ? 'ok' : gateResult.gate.action === 'escalate' ? 'bad' : 'warn'}>{gateResult.gate.action}</StatusChip>
                    <StatusChip tone={gateResult.gate.allowFix ? 'ok' : 'bad'} plain>allowFix {String(gateResult.gate.allowFix)}</StatusChip>
                    <StatusChip tone="neutral" plain>confidence {(gateResult.gate.topConfidence * 100).toFixed(0)}%</StatusChip>
                    <StatusChip tone="neutral" plain>coverage {(gateResult.gate.coverageRatio * 100).toFixed(0)}%</StatusChip>
                  </div>
                  {!gateResult.validation.valid && (
                    <Callout tone="bad">
                      <ul className="ui-list">{gateResult.validation.errors.map((x, i) => <li key={i}>{x}</li>)}</ul>
                    </Callout>
                  )}
                  {gateResult.gate.reasons.length > 0 && (
                    <ul className="ui-list ui-cell-sub">{gateResult.gate.reasons.map((x, i) => <li key={i}>{x}</li>)}</ul>
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </Drawer>
    </>
  );
}
